import {Component, forwardRef, inject, Input, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatInput} from "@angular/material/input";

import {ScheduleSettings} from "../../../models/schedulable-task-options.model";

@Component({
    selector: 'app-schedule-settings',
    templateUrl: './schedule-settings.component.html',
    styleUrls: ['./schedule-settings.component.scss'],
    imports: [
        MatSlideToggle,
        ReactiveFormsModule,
        MatFormField,
        MatSelect,
        MatOption,
        MatInput,
        MatLabel,
        MatError
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ScheduleSettingsComponent),
            multi: true
        }
    ]
})
export class ScheduleSettingsComponent implements OnInit {
    private fb = inject(FormBuilder);

    @Input() scheduleSettingsForm: FormGroup;
    @Input() scheduleSettings: ScheduleSettings;

    cronRegex = new RegExp(
        '^' +
        // Minute: 0-59, *, lists, ranges, steps
        '(?:(?:\\*(?:\\/\\d+)?|(?:[0-5]?\\d)(?:-(?:[0-5]?\\d))?(?:\\/\\d+)?)(?:,(?:\\*(?:\\/\\d+)?|(?:[0-5]?\\d)(?:-(?:[0-5]?\\d))?(?:\\/\\d+)?))*)\\s+' +
        // Hour: 0-23
        '(?:(?:\\*(?:\\/\\d+)?|(?:[01]?\\d|2[0-3])(?:-(?:[01]?\\d|2[0-3]))?(?:\\/\\d+)?)(?:,(?:\\*(?:\\/\\d+)?|(?:[01]?\\d|2[0-3])(?:-(?:[01]?\\d|2[0-3]))?(?:\\/\\d+)?))*)\\s+' +
        // Day of month: 1-31
        '(?:(?:\\*(?:\\/\\d+)?|(?:0?[1-9]|[12]\\d|3[01])(?:-(?:0?[1-9]|[12]\\d|3[01]))?(?:\\/\\d+)?)(?:,(?:\\*(?:\\/\\d+)?|(?:0?[1-9]|[12]\\d|3[01])(?:-(?:0?[1-9]|[12]\\d|3[01]))?(?:\\/\\d+)?))*)\\s+' +
        // Month: 1-12
        '(?:(?:\\*(?:\\/\\d+)?|(?:0?[1-9]|1[0-2])(?:-(?:0?[1-9]|1[0-2]))?(?:\\/\\d+)?)(?:,(?:\\*(?:\\/\\d+)?|(?:0?[1-9]|1[0-2])(?:-(?:0?[1-9]|1[0-2]))?(?:\\/\\d+)?))*)\\s+' +
        // Day of week: 0-7 or SUN-SAT
        '(?:(?:\\*(?:\\/\\d+)?|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(?:-(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT))?(?:\\/\\d+)?)(?:,(?:\\*(?:\\/\\d+)?|(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(?:-(?:[0-7]|SUN|MON|TUE|WED|THU|FRI|SAT))?(?:\\/\\d+)?))*)' +
        '$', 'i'
    );

    ngOnInit(): void {
        // Initialize your form with default values
        this.scheduleSettingsForm.addControl('scheduleEnabled', this.fb.control(this.scheduleSettings.scheduleEnabled));
        this.scheduleSettingsForm.addControl('schedulingMode', this.fb.control(this.scheduleSettings.schedulingMode));
        // The form-level validator requires a positive interval only for an enabled Interval schedule.
        // Keeping this control validator disabled-aware allows manual/disabled feeds to clear the stale value to 0.
        this.scheduleSettingsForm.addControl('intervalMinutes', this.fb.control(this.scheduleSettings.intervalMinutes));
        this.scheduleSettingsForm.addControl('cronExpression', this.fb.control(this.scheduleSettings.cronExpression, [Validators.pattern(this.cronRegex)]));
        this.scheduleSettingsForm.addControl('startDateUtc', this.fb.control(this.scheduleSettings.startDateUtc));
        this.scheduleSettingsForm.addControl('endDateUtc', this.fb.control(this.scheduleSettings.endDateUtc));
        this.scheduleSettingsForm.setValidators(this.validateSchedule);
        if (this.scheduleSettingsForm.get('scheduleEnabled')?.value !== true) {
            this.clearInactiveScheduleFields();
        }
        this.scheduleSettingsForm.get('scheduleEnabled')?.valueChanges.subscribe(enabled => {
            if (!enabled) {
                this.clearInactiveScheduleFields();
            }
            this.scheduleSettingsForm.updateValueAndValidity({emitEvent: false});
        });
    }

    private readonly validateSchedule = (control: AbstractControl): ValidationErrors | null => {
        if (control.get('scheduleEnabled')?.value !== true) return null;

        const mode = String(control.get('schedulingMode')?.value ?? '').trim();
        if (mode === 'Interval') {
            const interval = Number(control.get('intervalMinutes')?.value);
            if (!Number.isInteger(interval) || interval <= 0) return {scheduleIntervalInvalid: true};
        } else if (mode === 'Cron') {
            const cron = String(control.get('cronExpression')?.value ?? '').trim();
            if (!cron || !this.cronRegex.test(cron)) return {scheduleCronInvalid: true};
        } else {
            return {scheduleModeRequired: true};
        }

        const start = this.toDate(control.get('startDateUtc')?.value);
        const end = this.toDate(control.get('endDateUtc')?.value);
        if (start === undefined || end === undefined) return {scheduleDateInvalid: true};
        if (start && end && start.getTime() > end.getTime()) return {scheduleDateOrderInvalid: true};
        return null;
    };

    private clearInactiveScheduleFields(): void {
        this.scheduleSettingsForm.patchValue({
            schedulingMode: '',
            intervalMinutes: 0,
            cronExpression: '',
            startDateUtc: null,
            endDateUtc: null,
            nextExecutionUtc: null
        }, {emitEvent: false});
    }

    private toDate(value: unknown): Date | null | undefined {
        if (value === null || value === undefined || value === '') return null;
        const date = value instanceof Date ? value : new Date(String(value));
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
